import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useGetOrgGatesQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery as useGetIntakeStatusOrganizationQuery,
} from '@features/admin-v2/store/api';
import { AdminPayerConnections } from '@features/payer-connections/components/AdminPayerConnections';
import { Alert, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { IntakeAgreementPanel } from './components/four-stage/IntakeAgreementPanel';
import {
  INTAKE_STATUS_ADMIN_TABS,
  IntakeStatusAdmin,
  type IntakeStatusAdminTab,
} from './components/IntakeStatusAdmin';
import { intakeAgreementScope, useAdminIntakeAgreement } from './hooks/useIntakeAgreement';
import { AdminIntakeHubEmbedded } from './AdminIntakeHubEmbedded';

export function IntakeStatusAdminPage() {
  const { organizationId = '' } = useParams();
  return <ScopedIntakeStatusAdminPage key={organizationId} organizationId={organizationId} />;
}

function ScopedIntakeStatusAdminPage({ organizationId }: { organizationId: string }) {
  const [params, setParams] = useSearchParams();
  const { user, isAuthenticated, isLoading } = useAuth0();
  const roles: unknown = user?.['https://api.smartscribe.health/roles'];
  const authorized =
    isAuthenticated && !isLoading && Array.isArray(roles) && roles.includes('Admin');
  const payerEnabled = useFeatureGate(FeatureGates.PAYER_CONNECTIONS);
  const { currentData: gates, error: gateError } = useGetOrgGatesQuery(
    { organizationId },
    { skip: !authorized || !organizationId },
  );
  const enabled = Boolean(
    authorized &&
    organizationId &&
    !gateError &&
    gates?.gates.some(gate => gate.gate_id === FeatureGates.INTAKE_HUB_V3 && gate.evaluated_result),
  );
  const { currentData: organization } = useGetIntakeStatusOrganizationQuery(
    { organizationId },
    { skip: !enabled },
  );
  const agreement = useAdminIntakeAgreement(
    organizationId,
    intakeAgreementScope(organizationId, user?.sub ?? '', user?.org_id, roles),
    enabled && Boolean(user?.sub),
  );
  const selectedTab: IntakeStatusAdminTab =
    INTAKE_STATUS_ADMIN_TABS.find(tab => tab === params.get('statusTab')) ?? 'overview';
  const selectTab = (tab: IntakeStatusAdminTab) => {
    const next = new URLSearchParams(params);
    next.set('statusTab', tab);
    setParams(next);
  };
  if (isLoading || (authorized && !gates && !gateError))
    return <LinearProgress aria-label="Loading Intake Status" />;
  if (!authorized) return <Alert severity="error">Staff access is required.</Alert>;
  if (!enabled)
    return <Alert severity="info">Intake Hub V3 is not available for this practice.</Alert>;
  return (
    <Stack spacing={3} sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" component="h1">
        Intake Status Admin
      </Typography>
      {organization?.name && <Typography color="text.secondary">{organization.name}</Typography>}
      <Button
        href={`/admin-v2/organization/${encodeURIComponent(organizationId)}?tab=intake-hub`}
        sx={{ alignSelf: 'flex-start' }}
      >
        Back to practice
      </Button>
      <IntakeStatusAdmin
        selectedTab={selectedTab}
        onSelectTab={selectTab}
        overview={
          <Stack spacing={2}>
            <IntakeAgreementPanel agreement={agreement} staff />
            <Typography>
              Review this practice’s intake and verification in the Verification tab. Billing shows
              current payer connections.
            </Typography>
            <Button onClick={() => selectTab('verification')} sx={{ alignSelf: 'flex-start' }}>
              Review intake
            </Button>
          </Stack>
        }
        billing={
          selectedTab === 'billing' &&
          (payerEnabled ? (
            <AdminPayerConnections
              organizationId={organizationId}
              intakeReviewPath={`/admin-v2/intake-status/${encodeURIComponent(organizationId)}?statusTab=verification`}
            />
          ) : (
            <Alert severity="info">Payer connections are not enabled.</Alert>
          ))
        }
        verification={
          <AdminIntakeHubEmbedded
            organizationId={organizationId}
            providers={(organization?.users ?? [])
              .filter(member => member.status !== 'inactive')
              .map(member => ({
                user_id: member.user_id,
                name: member.name ?? '',
                email: member.email ?? '',
                roles: member.roles ?? [],
              }))}
            formOnly
            isActive={selectedTab === 'verification'}
          />
        }
      />
    </Stack>
  );
}
